import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  PdfViewerComponent,
  Toolbar,
  Magnification,
  Navigation,
  LinkAnnotation,
  BookmarkView,
  ThumbnailView,
  Print,
  TextSelection,
  TextSearch,
  Annotation,
  FormFields,
  FormDesigner,
  Inject,
} from '@syncfusion/ej2-react-pdfviewer';
import { getApiBaseUrl } from '../utils/apiUrl';

// Syncfusion CSS importieren
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-react-pdfviewer/styles/material.css';

// Syncfusion Lizenz registrieren
import { registerLicense } from '@syncfusion/ej2-base';

// Deutsche Lokalisierung importieren und initialisieren
import { initSyncfusionLocale } from '../utils/syncfusionLocale';

const SYNCFUSION_LICENSE_KEY = process.env.REACT_APP_SYNCFUSION_LICENSE_KEY || '';
if (SYNCFUSION_LICENSE_KEY) {
  registerLicense(SYNCFUSION_LICENSE_KEY);
}

// Deutsche Lokalisierung initialisieren
initSyncfusionLocale();

export interface SyncfusionPdfViewerHandle {
  insertSignature: (signatureBase64: string) => void;
  saveDocument: () => Promise<Blob | null>;
  downloadDocument: () => void;
  getAnnotations: () => any[];
}

interface SyncfusionPdfViewerProps {
  documentId: number;
  height?: string;
  onSignatureInserted?: () => void;
}

const SyncfusionPdfViewer = forwardRef<SyncfusionPdfViewerHandle, SyncfusionPdfViewerProps>(
  ({ documentId, height = '600px', onSignatureInserted }, ref) => {
    const viewerRef = useRef<PdfViewerComponent>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    const apiUrl = getApiBaseUrl();

    // PDF direkt vom Download-Endpoint laden
    const documentPath = `${apiUrl}/api/dokumente/${documentId}/download`;

    // CDN URL für die PDF Viewer Ressourcen (passend zur installierten Version 31.2.15)
    const resourceUrl = 'https://cdn.syncfusion.com/ej2/31.2.15/dist/ej2-pdfviewer-lib';

    useEffect(() => {
      console.log('SyncfusionPdfViewer: Lade PDF für Dokument', documentId);
      console.log('SyncfusionPdfViewer: Document URL:', documentPath);
      console.log('SyncfusionPdfViewer: Resource URL:', resourceUrl);
    }, [documentId, documentPath, resourceUrl]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      insertSignature: (signatureBase64: string) => {
        if (!viewerRef.current) {
          console.error('PDF Viewer nicht bereit');
          return;
        }

        try {
          const viewer = viewerRef.current as any;
          console.log('PDF Viewer insertSignature called with base64 length:', signatureBase64.length);

          // Registriere die Unterschrift als Custom Stamp
          // Syncfusion erfordert, dass Custom Stamps vor der Verwendung registriert werden
          viewer.customStampSettings = {
            enableCustomStamp: true,
            customStamps: [
              {
                customStampName: 'MeineUnterschrift',
                customStampImageSource: signatureBase64,
              }
            ]
          };

          console.log('Custom Stamp registriert');

          // Aktuelle Seite und Viewport ermitteln
          const currentPage = viewer.currentPageNumber;
          const pageInfo = viewer.getPageInfo(currentPage);
          const pageWidth = pageInfo?.width || 595;
          const pageHeight = pageInfo?.height || 842;

          console.log('Page info:', { currentPage, pageWidth, pageHeight });

          // Unterschrift-Position berechnen (unten rechts)
          const signatureWidth = 200;
          const signatureHeight = 80;
          const xPosition = pageWidth - signatureWidth - 50;
          const yPosition = pageHeight - signatureHeight - 100;

          // Versuche Custom Stamp programmatisch hinzuzufügen
          const customStampAnnotation = {
            pageNumber: currentPage,
            stampAnnotationType: 'Custom',
            isDynamicStamp: false,
            customStampName: 'MeineUnterschrift',
            customStampImageSource: signatureBase64,
            author: 'Benutzer',
            modifiedDate: new Date().toISOString(),
            subject: 'Unterschrift',
            opacity: 1,
            bounds: {
              x: xPosition,
              y: yPosition,
              width: signatureWidth,
              height: signatureHeight,
            },
          };

          try {
            viewer.annotation.addAnnotation('Stamp', customStampAnnotation);
            console.log('Custom Stamp Annotation hinzugefügt');
          } catch (stampErr) {
            console.log('Stamp Annotation fehlgeschlagen:', stampErr);
          }

          // Da Syncfusion keine direkte programmatische Einfügung von Custom Stamps unterstützt,
          // aktivieren wir den Custom Stamp Modus und informieren den Benutzer
          alert('Ihre Unterschrift wurde als Stempel geladen.\n\nBitte wählen Sie in der Toolbar das Stempel-Werkzeug (Stamp) und dann "Custom" aus der Liste.\nKlicken Sie anschließend auf die gewünschte Position im PDF.');

          // Aktiviere den Stamp-Annotation-Modus
          viewer.annotation.setAnnotationMode('Stamp');

          onSignatureInserted?.();

        } catch (err) {
          console.error('Fehler beim Einfügen der Unterschrift:', err);
          alert('Die Unterschrift konnte nicht automatisch eingefügt werden. Bitte nutzen Sie das Stempel-Werkzeug in der Toolbar.');
        }
      },

      saveDocument: async () => {
        if (!viewerRef.current) {
          console.error('PDF Viewer nicht bereit');
          return null;
        }

        try {
          const viewer = viewerRef.current;

          // PDF mit Annotationen als Blob exportieren
          const pdfBlob = await new Promise<Blob>((resolve, reject) => {
            viewer.saveAsBlob().then((blob: Blob) => {
              resolve(blob);
            }).catch((err: any) => {
              reject(err);
            });
          });

          return pdfBlob;
        } catch (err) {
          console.error('Fehler beim Speichern des PDFs:', err);
          return null;
        }
      },

      downloadDocument: () => {
        if (!viewerRef.current) {
          console.error('PDF Viewer nicht bereit');
          return;
        }

        try {
          viewerRef.current.download();
        } catch (err) {
          console.error('Fehler beim Download:', err);
        }
      },

      getAnnotations: () => {
        if (!viewerRef.current) {
          return [];
        }

        try {
          // Alle Annotationen aus dem Viewer holen
          const annotations = viewerRef.current.annotationCollection || [];
          return annotations;
        } catch (err) {
          console.error('Fehler beim Abrufen der Annotationen:', err);
          return [];
        }
      },
    }));

    const handleDocumentLoad = () => {
      console.log('SyncfusionPdfViewer: PDF erfolgreich geladen');
      setLoading(false);
      setError(null);
    };

    const handleDocumentLoadFailed = (args: any) => {
      console.error('SyncfusionPdfViewer: Fehler beim Laden des PDFs:', args);
      setLoading(false);
      setError('Fehler beim Laden des PDF-Dokuments');
    };

    if (error) {
      return (
        <div style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#d32f2f',
          backgroundColor: '#ffebee',
          borderRadius: '4px',
          padding: '16px'
        }}>
          Fehler: {error}
        </div>
      );
    }

    return (
      <div style={{ height, position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 10
          }}>
            PDF wird geladen...
          </div>
        )}
        <PdfViewerComponent
          ref={viewerRef}
          id={`pdfViewer_${documentId}`}
          documentPath={documentPath}
          resourceUrl={resourceUrl}
          height="100%"
          enableToolbar={true}
          enableNavigationToolbar={true}
          enableThumbnail={true}
          enableBookmark={true}
          enableTextSearch={true}
          enableTextSelection={true}
          enablePrint={true}
          enableDownload={true}
          enableAnnotation={true}
          enableAnnotationToolbar={true}
          enableHandwrittenSignature={true}
          enableStampAnnotations={true}
          enableCommentPanel={false}
          locale="de"
          documentLoad={handleDocumentLoad}
          documentLoadFailed={handleDocumentLoadFailed}
          toolbarSettings={{
            showTooltip: true,
            toolbarItems: [
              'PageNavigationTool',
              'MagnificationTool',
              'PanTool',
              'SelectionTool',
              'SearchOption',
              'PrintOption',
              'DownloadOption',
              'AnnotationEditTool',
              'FormDesignerEditTool',
            ],
            annotationToolbarItems: [
              'HandWrittenSignatureTool',
              'StampAnnotationTool',
              'InkAnnotationTool',
              'FreeTextAnnotationTool',
              'FontFamilyAnnotationTool',
              'FontSizeAnnotationTool',
              'FontStylesAnnotationTool',
              'FontColorAnnotationTool',
              'StrokeColorEditTool',
              'ThicknessEditTool',
              'OpacityEditTool',
              'AnnotationDeleteTool',
            ],
          }}
        >
          <Inject services={[
            Toolbar,
            Magnification,
            Navigation,
            LinkAnnotation,
            BookmarkView,
            ThumbnailView,
            Print,
            TextSelection,
            TextSearch,
            Annotation,
            FormFields,
            FormDesigner
          ]} />
        </PdfViewerComponent>
      </div>
    );
  }
);

SyncfusionPdfViewer.displayName = 'SyncfusionPdfViewer';

export default SyncfusionPdfViewer;
